import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useSessionTimeout, SessionTimeoutWarning } from './utils/sessionTimeout';
import Header from './components/Header';
import Landing from './pages/Landing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Home from './pages/Home';
import About from './pages/About';
import DemographicsDashboard from './pages/DemographicsDashboard';
import ChronicConditionsDashboard from './pages/ChronicConditionsDashboard';
import ERDashboard from './pages/ERDashboard';
import INPDashboard from './pages/INPDashboard';
import SNFDashboard from './pages/SNFDashboard';
import HHADashboard from './pages/HHADashboard';
import HOSDashboard from './pages/HOSDashboard';
import OPDashboard from './pages/OPDashboard';
import PHYDashboard from './pages/PHYDashboard';
import DMEDashboard from './pages/DMEDashboard';
import TCOCDashboardMain from './pages/TCOCDashboardMain';
import Login from './pages/Login';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function AppContent() {
  const { user, isLoading } = useAuth();
  const { showWarning, timeLeft, extendSession } = useSessionTimeout();
  
  const handleLoginSuccess = (userData) => {
    // Login.js already calls login(user, token) directly — nothing to do here
  };
  
  if (isLoading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  // If not logged in, show Landing or Login pages
  if (!user) {
    return (
      <Routes>
        <Route path="/landing" element={<Landing />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        <Route path="*" element={<Navigate to="/landing" replace />} />
      </Routes>
    );
  }

  // If logged in, show dashboard with header and session timeout warning
  return (
    <>
      <SessionTimeoutWarning 
        show={showWarning} 
        timeLeft={timeLeft} 
        onExtend={extendSession} 
      />
      <div className="app">
        <Header />
        <div className="app-body">
        <Routes>
          <Route path="/landing" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route 
            path="/demographics" 
            element={
              <ProtectedRoute>
                <DemographicsDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/chronic-conditions" 
            element={
              <ProtectedRoute>
                <ChronicConditionsDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/er" 
            element={
              <ProtectedRoute>
                <ERDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/inp" 
            element={
              <ProtectedRoute>
                <INPDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/snf" 
            element={
              <ProtectedRoute>
                <SNFDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/hha" 
            element={
              <ProtectedRoute>
                <HHADashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/hos" 
            element={
              <ProtectedRoute>
                <HOSDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/op" 
            element={
              <ProtectedRoute>
                <OPDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/phy" 
            element={
              <ProtectedRoute>
                <PHYDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dme" 
            element={
              <ProtectedRoute>
                <DMEDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tcoc" 
            element={
              <ProtectedRoute>
                <TCOCDashboardMain />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/analytics" 
            element={
              <ProtectedRoute>
                <div className="page-content">
                  <h1>Analytics Dashboard - Coming Soon</h1>
                </div>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/reports" 
            element={
              <ProtectedRoute>
                <div className="page-content">
                  <h1>Reports Dashboard - Coming Soon</h1>
                </div>
              </ProtectedRoute>
            } 
          />
        </Routes>
      </div>
    </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
